'use client';


import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useRef, useMemo } from 'react';
import { useProductInteraction } from '@/app/(users)/totem-by-slug/[slug]/hooks/use-product-interaction';
import { useTotem } from '@/app/(users)/totem-by-slug/[slug]/providers/totem-provider';
import type { MenuSnapshotProduct } from '@/app/(users)/totem-by-slug/[slug]/types/menu-snapshot';
import { getProductDisplayName } from '@/app/(users)/totem-by-slug/[slug]/utils/product-name';
import { formatTotemPrice } from '@/app/(users)/totem-by-slug/[slug]/utils/totem-price';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

type ProductCardProps = {
  product: MenuSnapshotProduct;
  onSelect: (product: MenuSnapshotProduct, rect: DOMRect) => void;
  compact?: boolean;
};

function TotemProductCard({ product, onSelect, compact }: ProductCardProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const name = getProductDisplayName(product);

  const priceFrom = useMemo(() => {
    if (Number(product.price) > 0) return 0;
    let min = 0;
    for (const ps of product.product_subselection ?? []) {
      const sub = ps.subselection;
      if (!sub) continue;
      for (const item of sub.subselection_item ?? []) {
        const isOutOfStock = item.product_stock?.stock === 0;
        if (!isOutOfStock && item.price > 0 && (min === 0 || item.price < min)) {
          min = item.price;
        }
      }
    }
    return min;
  }, [product]);

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex flex-col items-center text-center transition-opacity hover:opacity-80 rounded-xl',
        compact ? 'gap-1 p-1 sm:gap-1.5 sm:p-1 lg:gap-3 lg:p-2' : 'gap-3 p-2'
      )}
      onClick={() => {
        const rect = ref.current?.getBoundingClientRect();
        if (rect) onSelect(product, rect);
      }}
    >
      <div
        className={cn(
          'relative aspect-square w-full overflow-hidden rounded-xl bg-white dark:bg-muted/30',
          compact ? 'max-w-24 sm:max-w-28 lg:max-w-32 xl:max-w-48' : 'max-w-48'
        )}
      >
        {product.imageurl ? (
          <Image
            alt={name}
            className="object-contain p-2"
            fill
            sizes="(max-width: 768px) 33vw, 200px"
            src={product.imageurl}
          />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">{name.slice(0, 1)}</div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <p className={cn('font-medium capitalize', compact ? 'text-[11px] leading-tight sm:text-xs lg:text-sm' : 'text-sm')}>{name}</p>
        <p
          className={cn(
            'font-bold text-amber-600',
            compact ? 'text-xs sm:text-sm lg:text-base' : 'text-base'
          )}
        >
          {Number(product.price) === 0 && priceFrom > 0
            ? `Desde ${formatTotemPrice(priceFrom)}`
            : formatTotemPrice(Number(product.price))}
        </p>
      </div>
    </button>
  );
}

export function TotemProductGrid() {
  const pathname = usePathname();
  const isDashboard = pathname?.includes('/takeorder');
  const compact = true;

  const { activeCategoryName, activeCategoryId, menu, searchQuery, setSearchQuery } = useTotem();
  const { handleProductClick } = useProductInteraction();

  const activeSearch = searchQuery.trim().toLowerCase();

  const products =
    menu?.products.filter(product => {
      if (product.product_stock?.stock === 0) return false;
      const matchesSearch = !activeSearch || getProductDisplayName(product).toLowerCase().includes(activeSearch);
      
      // If there's an active search, ignore category filter (global search)
      if (activeSearch) {
        return matchesSearch;
      }
      
      // Otherwise, filter by the active category
      return product.product_category?.some(pc => pc.category_id === activeCategoryId);
    }) ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white dark:bg-transparent">
      <div
        className={cn(
          'shrink-0 px-6 pt-6 items-center min-h-[4rem] gap-4',
          compact ? 'flex flex-col sm:grid sm:grid-cols-3' : 'grid grid-cols-3'
        )}
      >
        <h2
          className={cn(
            'font-bold truncate w-full text-left',
            compact ? 'text-lg sm:text-2xl' : 'text-2xl'
          )}
        >
          {activeSearch ? `Resultados para "${searchQuery}" (${products.length})` : activeCategoryName}
        </h2>

        {/* Empty div for right column balance */}
        <div />
      </div>
      <ScrollArea className="min-h-0 flex-1 px-6 pb-6 pt-4">
        {products.length === 0 ? (
          <p className="text-muted-foreground">No hay productos en esta categoría.</p>
        ) : (
          <div className={cn("grid", compact ? "grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5" : "grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3")}>
            {products.map(product => (
              <TotemProductCard
                key={product.id}
                product={product}
                compact={compact}
                onSelect={(p, rect) => handleProductClick(p, rect)}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
